import * as turf from "@turf/turf";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Geocoder,
  fetchOverpassTurbo,
  isValidGeoJSON,
  useModals,
} from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import { geminiRequest, searchNominatim, useAuthContext } from "senf-shared";
import { v4 as uuidv4 } from "uuid";
import GeoJSONUploader from "./GeoJSONUploader";

const PlanningareaBuilder = ({
  formik,
  setDrawMode,
  drawMode,
  featureId,
  statefulMap,
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const handleMergeFeatures = (data) => {
    if (data?.features) {
      // Filter valid polygons
      const validFeatures = data.features.filter(
        (feature) =>
          feature.geometry &&
          (feature.geometry.type === "Polygon" ||
            feature.geometry.type === "MultiPolygon"),
      );

      if (validFeatures.length === 0) {
        return;
      }

      // If there's only one feature, use it directly
      if (validFeatures.length === 1) {
        formik.setFieldValue("mapbounds", {
          type: "FeatureCollection",
          features: validFeatures,
        });
        return;
      }

      // For multiple features, merge them using union
      const merged = validFeatures.reduce((acc, feature) => {
        if (!acc) return feature;
        try {
          return turf.union(acc, feature);
        } catch (error) {
          console.error("Error merging features:", error);
          return acc;
        }
      }, null);

      const mapbounds = merged
        ? {
            type: "FeatureCollection",
            features: [merged],
          }
        : {
            type: "FeatureCollection",
            features: [],
          };

      formik.setFieldValue("mapbounds", mapbounds);
    }
  };

  const handleSearch = async (result) => {
    if (!result) return;

    try {
      console.log("Searching for:", result);

      // Extract state and country from context
      const countryCode = result.context?.find((item) =>
        item.id?.includes("country"),
      )?.short_code;
      const stateCode = result.context?.find((item) =>
        item.id?.includes("region"),
      )?.short_code;

      console.log("Country:", countryCode);
      console.log("State:", stateCode);

      const features = await searchNominatim(result.text, {
        filterTypes: [
          "administrative",
          "city",
          "suburb",
          "postcode",
          "boundary",
        ],
        countryCode,
        stateCode,
      });

      if (features.length === 0) {
        console.log(result, "countryCode", countryCode, "stateCode", stateCode);

        const overPassCode = await geminiRequest({
          assistantId: "default",
          body: `
  Generate a **fully correct Overpass Turbo query** that finds the **most specific** administrative boundary for a given place.


    **Inputs:**
  - Place name: ${result.place_name}
  - Country code: ${countryCode}
  - State code: ${stateCode}

 ---
 **🔹 STRICT RULES (FOLLOW EXACTLY):**
  1. **Query ALL available administrative boundaries** for the given place:
     - Use: \`relation["boundary"="administrative"]["name"="..."]\`
     - Do **NOT** hardcode \`admin_level="6"\`, \`admin_level="8"\`, etc.
     - Retrieve **ALL** admin levels for the place.
  2. **From the results, find the most specific boundary**:
     - The **most specific** admin level has the **highest number**.
     - Example: If İzmir has admin_level=4, 6, 8, 9', the query **must return admin_level=9**.
  3. **Do NOT use any of these invalid filters:**
     - ❌ \`area[name="..."]\` (invalid for relations)
     - ❌ \`area.a\`, \`around.a\`, \`within\`
     - ❌ \`ISO3166-2\`
     - ❌ \`if:t["admin_level"]\` (Overpass does not support dynamic filtering)
     - ❌ \`order by t["admin_level"] desc\` (Overpass does not support sorting)
  4. **Return the Overpass query in this format (NO EXTRA TEXT):**
     \`\`\`overpass
     (Overpass Turbo Query)
     \`\`\`

  ---
  **Example Correct Queries:**
  
  **For İzmir (Turkey, most specific admin level 9)**:
  \`\`\`overpass
  [out:json][timeout:25];
  (
    relation["boundary"="administrative"]["name"="İzmir"]["admin_level"="9"];
  );
  out geom;
  \`\`\`

  **For Berlin (Germany, most specific admin level 10)**:
  \`\`\`overpass
  [out:json][timeout:25];
  (
    relation["boundary"="administrative"]["name"="Berlin"]["admin_level"="10"];
  );
  out geom;
  \`\`\`
  
  **For New York City (USA, most specific admin level 8)**:
  \`\`\`overpass
  [out:json][timeout:25];
  (
    relation["boundary"="administrative"]["name"="New York"]["admin_level"="8"];
  );
  out geom;
  \`\`\`
  `,
          lang: "en",
        });

        // Extract the Overpass query from the response
        const overpassQuery =
          overPassCode.match(/```overpass\n([\s\S]+?)\n```/)?.[1] || "";

        console.log("overPassCode", overPassCode);

        const overPassFeatures = await fetchOverpassTurbo(overpassQuery);

        console.log(overPassFeatures);
        if (overPassFeatures.features.length > 0) {
          const selectedFeature = overPassFeatures.features[0];
          handleAddBoundary([selectedFeature]);
          return;
        }

        openModal(null, {
          title: t("map:builder.planningArea.noPlanningAreaFound.title"),
          description: t(
            "map:builder.planningArea.noPlanningAreaFound.description",
          ),
          zIndex: 999999999999999,
          id: "noPlanningAreaFoundModal",
        });
        return;
      }

      // Sort features by admin_level to get the most specific boundary first
      const sortedFeatures = features.sort((a, b) => {
        const aLevel = Number.parseInt(a.properties.admin_level) || 0;
        const bLevel = Number.parseInt(b.properties.admin_level) || 0;
        return bLevel - aLevel; // Higher admin_level means more specific
      });

      const selectedFeature = sortedFeatures[0];
      console.log("Selected feature:", selectedFeature);

      // Add the selected feature
      handleAddBoundary([selectedFeature]);
    } catch (error) {
      console.error("Error fetching boundaries:", error);
      alert(t("global:errorFetchingBoundaries"));
    } finally {
    }
  };

  const handleAddBoundary = (features) => {
    // Split MultiPolygons into individual Polygons
    const splitFeatures = features.flatMap((feature) => {
      if (feature.geometry.type === "MultiPolygon") {
        // Convert each polygon in the MultiPolygon to a separate feature
        return feature.geometry.coordinates.map((coordinates) => ({
          type: "Feature",
          id: uuidv4(),
          properties: { ...feature.properties },
          geometry: {
            type: "Polygon",
            coordinates: coordinates,
          },
        }));
      }
      // For regular Polygons, just add an ID if not present
      return [
        {
          ...feature,
          id: feature.id || uuidv4(),
        },
      ];
    });

    const geoJson = {
      type: "FeatureCollection",
      features: splitFeatures,
    };

    if (isValidGeoJSON(JSON.stringify(geoJson))) {
      if (formik.values.mapbounds) {
        // Add new features to existing ones, keeping them separate
        geoJson.features = [
          ...geoJson.features,
          ...formik.values.mapbounds.features,
        ];
      }
      // Set the value directly without merging
      formik.setFieldValue("mapbounds", geoJson);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 ">
        <p className="body-md">{t("map:builder.planningArea.infoText")}</p>
      </div>
      <div className="mt-3 flex flex-col justify-center gap-4">
        <Geocoder
          callback={handleSearch}
          placeholder={t("util:search", { val: t("global:planningAreaShort") })}
          geolocate={false}
          types="region,postcode,district,place,locality,neighborhood"
        />

        <div className="flex gap-2 w-full">
          <div className="flex-1">
            <GeoJSONUploader
              text={t("util:upload", {
                val: t("global:planningAreaShort"),
              })}
              fileTypes={[".geojson"]}
              fileTypesText="GeoJSON"
              isButton={true}
              size="lg"
              width="max"
              className="w-full"
              classNameWrapper="w-full"
              onFileUpload={({ data }) => {
                // Check if data and data.features are defined to avoid errors
                if (isValidGeoJSON(JSON.stringify(data))) {
                  if (formik.values.mapbounds) {
                    data.features = [
                      ...data.features,
                      ...formik.values.mapbounds.features,
                    ];

                    handleMergeFeatures(data);
                  } else {
                    handleMergeFeatures(data);
                  }
                } else {
                  alert(t("global:invalidGeoJson"));
                }
              }}
            />
          </div>
          <div className="flex-1">
            <Button
              disabled={drawMode.drawType === "mapbounds" || featureId}
              text={t("util:draw", { val: t("global:planningAreaShort") })}
              width="max"
              size="lg"
              leadingIcon="edit"
              classNameWrapper="w-full"
              onClick={() => {
                setDrawMode({ mode: "draw", drawType: "mapbounds" });
              }}
            />
          </div>
        </div>

        {/* {user?.isSuperAdmin && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-4">
              <input type="text" placeholder="search" />
              <Button text="search"  onClick={() => {
                handleSearch({text: "Berlin"})
              }}/>

             

              {foundGeoJsons.length > 0 && (
                <div className="mt-4 flex flex-col gap-2">
                  <p className="body-md font-semibold">Found Boundaries:</p>
                  {foundGeoJsons.map((group) => (
                    <div key={`${group.adminLevel}-${group.name}`} className="flex flex-col gap-1 border border-gray-200 rounded p-2">
                      <div className="flex justify-between items-center">
                        <p className="body-sm">
                          {group.name} (Level {group.adminLevel})
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          text="Add"
                          onClick={() => handleAddBoundary(group.features)}
                        />
                      </div>
                      <p className="text-sm text-gray-500">
                        {group.features.length} boundaries found
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}  */}
      </div>
    </div>
  );
};

export default PlanningareaBuilder;
